// Apolo IDE — Specs surface (sprint hgmq-finish, P7-T05 / Track C).
//
// Renders the durable spec (Kiro 3-file) from the Glass Box: Requirements →
// Design → Tasks, with EARS coverage + durable/stale state. Reads code.specs
// from the store. Presentational; "open in editor" dispatches apolo:openPath
// for the human to git-diff the on-disk markdown.

import { useState } from 'react';
import { FileText, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { specGlassBoxView } from '../../../lib/specs/specModel.js';

const ARTIFACTS = [
  { id: 'requirements', label: 'Requirements' },
  { id: 'design', label: 'Design' },
  { id: 'tasks', label: 'Tasks' },
];

export default function SpecsPanel({ sessionId }) {
  const spec = useAgentStore((s) => s.code.specs);
  const [tab, setTab] = useState('requirements');

  if (!spec) {
    return (
      <p className="rx-tp-empty" data-testid="specs-empty">
        O spec durável aparece aqui quando você aprova um plano com tasks. Fica
        efêmero no Glass Box durante o turno e é gravado em
        <code className="rx-chip" style={{ margin: '0 4px' }}>.apolo/specs/</code>
        só na aprovação (Requirements → Design → Tasks, git-diffável).
      </p>
    );
  }

  const view = specGlassBoxView(spec);
  const md = spec[tab]?.md ?? '';
  const staleFor = (id) => (id === 'design' && view.stale.design) || (id === 'tasks' && view.stale.tasks);
  const openPath = (id) => {
    if (typeof window === 'undefined' || !spec.paths) return;
    window.dispatchEvent(new CustomEvent('apolo:openPath', { detail: { sessionId, path: spec.paths[id] } }));
  };

  return (
    <section className="rx-tp-specs" aria-label="Spec durável" data-testid="specs-panel">
      <div className="rx-tp-sec" data-testid="specs-heading">
        Spec · {view.feature || 'feature'}
      </div>

      {/* meters */}
      <div className="rx-tp-ndep" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginBottom: 8 }}>
        <span data-testid="specs-ears"><span className="rx-tp-b">EARS:</span> {view.earsClauses} clauses</span>
        <span data-testid="specs-taskcount"><span className="rx-tp-b">tasks:</span> {view.taskCount}</span>
        <span
          className="rx-badge"
          data-testid="specs-durable"
          data-durable={view.durable ? 'true' : 'false'}
        >
          {view.durable
            ? (<><CheckCircle2 size={10} aria-hidden="true" /> durable</>)
            : 'ephemeral'}
        </span>
      </div>

      {/* tab strip */}
      <div role="tablist" aria-label="Artefatos do spec" className="flex items-center gap-1" style={{ borderBottom: '1px solid var(--rx-border)' }}>
        {ARTIFACTS.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={tab === a.id}
            className="rx-filetab"
            data-testid={`spec-tab-${a.id}`}
            onClick={() => setTab(a.id)}
          >
            <FileText size={11} aria-hidden="true" />
            {a.label}
            {staleFor(a.id) && <AlertTriangle size={10} aria-hidden="true" style={{ color: 'var(--rx-amber)' }} />}
          </button>
        ))}
        {spec.paths && (
          <button
            type="button"
            className="rx-btn"
            data-testid="spec-open"
            onClick={() => openPath(tab)}
            style={{ marginLeft: 'auto', padding: '3px 8px', fontSize: '10.5px' }}
          >
            <ExternalLink size={10} aria-hidden="true" />
            abrir
          </button>
        )}
      </div>

      {staleFor(tab) && (
        <p className="rx-tp-empty" data-testid="spec-stale" style={{ padding: '6px 4px 0', textAlign: 'left', color: 'var(--rx-amber)' }}>
          stale — editado upstream; regenere/sincronize antes de confiar.
        </p>
      )}

      <pre
        className="rx-codeblock-body"
        data-testid={`spec-body-${tab}`}
        style={{ margin: '8px 0 0', padding: '8px 4px', whiteSpace: 'pre-wrap', fontSize: '11.5px', color: 'var(--rx-tx2)' }}
      >
        {md}
      </pre>
    </section>
  );
}
